import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  Tabs,
  Tab,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddIcon from '@mui/icons-material/Add';
import EinstellungenSeitenKopf from '../../components';
import getLetterTemplates from '../../../../Apollo/queries/getLetterTemplates';
import deleteLetterTemplateMutation from '../../../../Apollo/mutations/deleteLetterTemplate';
import { PagePathname } from '../../../../routes/paths';

interface LetterTemplate {
  id: number;
  name: string;
  description: string | null;
  content: string;
  variables_used: string[] | null;
  created_at: string;
  updated_at: string;
}

const VERTRAGSVORLAGEN_BEREICH = 'vertragsvorlagen';
const VERTRAGSVORLAGEN_PLACEHOLDER =
  'Noch keine Vertragsvorlagen vorhanden. Die Verwaltung der Vertragsvorlagen folgt in einem späteren Schritt.';

/** Briefvorlagen-Liste inkl. Abfrage — nur mounten, wenn der Brief-Reiter aktiv ist. */
function BriefvorlagenPanel() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<LetterTemplate | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const { data, refetch } = useQuery(getLetterTemplates, {
    variables: { search: searchTerm || undefined },
    fetchPolicy: 'network-only',
  });

  const [deleteTemplate] = useMutation(deleteLetterTemplateMutation);

  const templates: LetterTemplate[] = data?.getLetterTemplates || [];

  useEffect(() => {
    setPage(0);
  }, [templates.length, searchTerm]);

  const pagedTemplates =
    rowsPerPage === -1 ? templates : templates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const getPreviewText = (content: string): string => {
    const text = stripHtml(content);
    return text.length > 80 ? `${text.substring(0, 80)}...` : text;
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleDelete = async () => {
    if (!templateToDelete) return;
    await deleteTemplate({ variables: { id: templateToDelete.id } });
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
    refetch();
  };

  const openDeleteDialog = (template: LetterTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Vorlagen
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Verwalte wiederverwendbare E-Mail- und Dokumentvorlagen
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(PagePathname.briefvorlagenNeu)}
          sx={{
            bgcolor: '#4ADE80',
            color: '#000',
            fontWeight: 'bold',
            '&:hover': { bgcolor: '#3CC970' },
          }}
        >
          Neue Vorlage
        </Button>
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Vorlagen durchsuchen..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Table or Empty State */}
      {templates.length === 0 && !searchTerm ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <MailOutlineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Noch keine Vorlagen vorhanden
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(PagePathname.briefvorlagenNeu)}
            sx={{
              bgcolor: '#4ADE80',
              color: '#000',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#3CC970' },
            }}
          >
            Erste Vorlage erstellen
          </Button>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    Vorschau
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                  >
                    Zuletzt geändert
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                    }}
                    align="right"
                  >
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedTemplates.map((template) => (
                  <TableRow key={template.id} sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'default' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MailOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography sx={{ fontWeight: 'bold' }}>{template.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {getPreviewText(template.content)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem' }}>{formatDate(template.updated_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() =>
                            navigate(PagePathname.briefvorlagenBearbeiten.replace(':id', String(template.id)))
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Bearbeiten
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(template)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={templates.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'Alle', value: -1 }]}
            labelRowsPerPage="Zeilen pro Seite"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} von ${count}`}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          />

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            {templates.length} Vorlagen gesamt
          </Typography>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Vorlage löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchtest du die Vorlage &bdquo;{templateToDelete?.name}&ldquo; wirklich löschen? Diese Aktion kann nicht
            rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Abbrechen
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function LetterTemplateList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isVertragsvorlagen = searchParams.get('bereich') === VERTRAGSVORLAGEN_BEREICH;
  const tabIndex = isVertragsvorlagen ? 1 : 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 1) {
      setSearchParams({ bereich: VERTRAGSVORLAGEN_BEREICH }, { replace: true });
    } else {
      // Briefvorlagen: Parameter vollständig entfernen — nie ?bereich=briefvorlagen
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <EinstellungenSeitenKopf title="Vorlagen" />

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Briefvorlagen" id="vorlagen-tab-0" aria-controls="vorlagen-tabpanel-0" />
        <Tab label="Vertragsvorlagen" id="vorlagen-tab-1" aria-controls="vorlagen-tabpanel-1" />
      </Tabs>

      {/* Conditional mount: inaktives Panel wird nicht gerendert (keine Vorlagen-Abfrage). */}
      {tabIndex === 0 && (
        <Box role="tabpanel" id="vorlagen-tabpanel-0" aria-labelledby="vorlagen-tab-0">
          <BriefvorlagenPanel />
        </Box>
      )}
      {tabIndex === 1 && (
        <Box role="tabpanel" id="vorlagen-tabpanel-1" aria-labelledby="vorlagen-tab-1">
          <Typography>{VERTRAGSVORLAGEN_PLACEHOLDER}</Typography>
        </Box>
      )}
    </Box>
  );
}

export default LetterTemplateList;
